import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, Settings, LogOut, Search, TrendingUp, TrendingDown, Command, Bell, X } from 'lucide-react';
import { useThemeStore } from '../../state/themeStore';
import { useAuthStore } from '../../state/authStore';
import { useAlertStore } from '../../state/alertStore';
import CreditsPill from '../credits/CreditsPill';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOP_TICKERS = ['NIFTY 50', 'SENSEX', 'NIFTYBEES'];

function TickerPill({ symbol, data }) {
  if (!data) return null;
  const isPositive = data.price_change >= 0;
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-secondary/80 text-[13px]">
      <span className="font-semibold text-foreground">{symbol}</span>
      <span className={`font-bold ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
        {Number(data.current_price).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </span>
      <span className={`flex items-center gap-0.5 font-medium ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
        {isPositive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
        {isPositive ? '+' : ''}{Number(data.price_change_pct).toFixed(2)}%
      </span>
    </div>
  );
}

export default function GlobalTopBar() {
  const { isDarkMode, toggleTheme } = useThemeStore();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [topTickerPrices, setTopTickerPrices] = useState({});
  const { unreadCount, events, panelOpen, togglePanel, fetchEvents, markRead, markAllRead } = useAlertStore();

  useEffect(() => {
    const fetchTopTickerPrices = async () => {
      try {
        const response = await axios.get(`${API}/top-tickers-prices`);
        if (response.data?.data) setTopTickerPrices(response.data.data);
      } catch { /* silently fail */ }
    };
    fetchTopTickerPrices();
    fetchEvents();
    const timer = setInterval(fetchTopTickerPrices, 30000);
    const alertTimer = setInterval(fetchEvents, 60000);
    return () => { clearInterval(timer); clearInterval(alertTimer); };
  }, []);

  const handleSearchClick = useCallback(() => {
    // Open the global command palette (Cmd+K) instead of jumping to the
    // screener page — lets the user type RELIANCE / HDFCBANK INTEL /
    // HOME without leaving their current view.
    window.dispatchEvent(new CustomEvent('kubair:open-command-palette'));
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/', { replace: true });
  }, [logout, navigate]);

  return (
    <header className="h-12 border-b border-border/60 bg-background/80 backdrop-blur-xl flex items-center justify-between px-4 shrink-0 z-40 sticky top-0">
      {/* Left: Logo */}
      <div className="flex items-center gap-3 min-w-[140px]">
        <button onClick={() => navigate('/public/home')} className="flex items-center gap-2 group">
          <img
            src="/kubair-mark.png"
            alt="KubAIr"
            className="w-9 h-9 object-contain drop-shadow-[0_0_10px_rgba(56,189,248,0.3)] group-hover:drop-shadow-[0_0_14px_rgba(56,189,248,0.5)] transition"
          />
          <span className="text-sm font-bold text-foreground transition-colors hidden sm:block tracking-tight">
            Kub<span className="bg-gradient-to-r from-emerald-500 via-sky-500 to-violet-500 bg-clip-text text-transparent">AI</span>r
          </span>
        </button>
      </div>

      {/* Center: Search bar trigger */}
      <button
        onClick={handleSearchClick}
        className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-secondary/50 hover:bg-secondary text-muted-foreground text-sm w-72 transition-colors"
        title="Open command palette (Cmd+K): RELIANCE, RELIANCE INTEL, HOME, AI…"
      >
        <Search className="w-3.5 h-3.5" />
        <span className="flex-1 text-left text-xs">Search stocks or type a command…</span>
        <kbd className="hidden lg:flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-background rounded border border-border">
          <Command className="w-2.5 h-2.5" />K
        </kbd>
      </button>

      {/* Right: Tickers + Actions */}
      <div className="flex items-center gap-2">
        {/* Compact ticker strip */}
        <div className="hidden md:flex items-center gap-1">
          {TOP_TICKERS.map((sym) => (
            <TickerPill key={sym} symbol={sym} data={topTickerPrices[sym]} />
          ))}
        </div>

        <div className="w-px h-5 bg-border mx-1 hidden md:block" />

        <CreditsPill />

        <div className="relative">
          <button
            onClick={togglePanel}
            className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors relative"
            title="Notifications"
          >
            <Bell className="w-4 h-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[9px] text-white font-bold flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {panelOpen && (
            <div className="absolute right-0 top-10 w-80 max-h-96 bg-popover border border-border rounded-lg shadow-xl z-50 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <span className="text-xs font-semibold">Notifications</span>
                <div className="flex items-center gap-1">
                  {unreadCount > 0 && (
                    <button onClick={() => markAllRead()} className="text-[10px] text-emerald-600 hover:underline">
                      Mark all read
                    </button>
                  )}
                  <button onClick={togglePanel} className="p-1 hover:bg-muted rounded">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-72">
                {events.length === 0 ? (
                  <div className="py-8 text-center text-xs text-muted-foreground">No notifications yet</div>
                ) : (
                  events.slice(0, 20).map((evt) => (
                    <button
                      key={evt.id}
                      onClick={() => { markRead(evt.id); if (evt.symbol) navigate(`/public/india-company/${evt.symbol}`); }}
                      className={`w-full text-left px-3 py-2 border-b border-border/50 hover:bg-muted/50 transition-colors ${!evt.is_read ? 'bg-emerald-500/5' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        {!evt.is_read && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />}
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate">{evt.title}</p>
                          <p className="text-[10px] text-muted-foreground line-clamp-2">{evt.message}</p>
                          <p className="text-[9px] text-muted-foreground mt-0.5">
                            {evt.created_at ? new Date(evt.created_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
              <div className="px-3 py-2 border-t border-border">
                <button
                  onClick={() => { togglePanel(); navigate('/public/alerts'); }}
                  className="text-xs text-emerald-600 hover:underline w-full text-center"
                >
                  Manage Alert Rules
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={toggleTheme}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          title={isDarkMode ? 'Light mode' : 'Dark mode'}
        >
          {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>

        <button
          onClick={() => navigate('/settings')}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          title="Settings"
        >
          <Settings className="w-4 h-4" />
        </button>

        {user && (
          <>
            <div className="w-px h-5 bg-border mx-0.5" />
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white text-xs font-bold shadow-sm shadow-emerald-500/20">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <button
                onClick={handleLogout}
                className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                title="Logout"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
